import { useState, useEffect } from "react";
import { inject } from "mobx-react";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import withRouter from "./withRouter";
import UploadErrorRow from "./upload-error-row";
import { fetch } from "../utils/api-wrapper";
import { getSavedExpenses, clearExpenses } from "../utils/offline-storage";
import { duplicateUploadMessage } from "../utils/expense-duplicate-message";

const OfflineExpenseUpload = ({data, navigate}) => {
  const [newExpensesAccountId, setNewExpensesAccountId] = useState(null);
  // XPEN-3594: saved receipts XPENS refused as duplicates, as {text, reportId} rows.
  const [offlineDuplicates, setOfflineDuplicates] = useState([]);

  useEffect(() => {
    getSavedExpenses((expenses) => {
      if (expenses.length > 0) {
        uploadOfflineExpenses(expenses);
      }
    }, (error) => {
      //console.error("Error retrieving saved expenses:", error);
    });
  // Once per mount on purpose: running again would send the saved receipts twice.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadOfflineExpenses = (expenses) => {
    fetch('/expenses_account/', 'POST', '{}',
      response => {
        const accountId = response.data.expenses_account_id;

        if (accountId) {
          const expensesUploaded = [];
          const duplicates = [];
          let otherFailures = 0;

          Promise.all(expenses.map(async expense => {
            const formData = new FormData();
            formData.append("image", expense.file);
            formData.append("description", expense.description);
            formData.append("is_billable", expense.isBillable);
            if (expense.isBillable) {
              formData.append("matter_id", expense.matterId);
            }

            await fetch(`/expenses_account/add/${accountId}`, "PUT", formData,
              () => expensesUploaded.push(expense.description),
              response => {
                const duplicate = duplicateUploadMessage(data, response?.response?.data, accountId);
                if (duplicate) {
                  const label = expense.description || expense.file?.name || "";
                  duplicates.push({ text: label + " : " + duplicate.text, reportId: duplicate.reportId });
                } else {
                  otherFailures += 1;
                }
              },
              true
            );
          })).then(() => {
            if (expensesUploaded.length > 0) {
              setNewExpensesAccountId(accountId);
            }
            // The store can only be emptied as a whole. A receipt that failed for another
            // reason is kept for the next launch unless something else was uploaded (as
            // before XPEN-3594); receipts XPENS already has are never worth resending.
            if (expensesUploaded.length > 0 || (duplicates.length > 0 && otherFailures === 0)) {
              clearExpenses();
            }
            if (duplicates.length > 0) {
              setOfflineDuplicates(duplicates);
            }
          });
        }
      }
    );
  };

  const viewUploadedExpenses = () => {
    navigate(`/expenses_account/${newExpensesAccountId}`);
    setNewExpensesAccountId(null);
  };

  const duplicatesTitle = offlineDuplicates.length === 1
    ? data.t.read('offline.error_duplicates_not_sent_one')
    : data.t.read('offline.error_duplicates_not_sent_other')?.replace('{n}', String(offlineDuplicates.length));

  return <>
    <Snackbar
      open={newExpensesAccountId !== null}
      autoHideDuration={5000}
      onClose={(_event, reason) => {
        // A click on the duplicate notice must not also dismiss View, the only way to the
        // expense account that did receive receipts. The timer still closes it.
        if (reason === 'clickaway' && offlineDuplicates.length > 0) return;
        setNewExpensesAccountId(null);
      }}
      message={data.t.read('offline.expenses_uploaded')}
      action={
        <button className="btn btn-thin text-primary font-weight-bold text-uppercase" onClick={viewUploadedExpenses} data-testid="view-uploaded-expenses">
          {data.t.read('offline.view_uploaded_expenses')}
        </button>
      }
      ContentProps={{ classes: { root: "offline-expense-upload-snackbar flex-nowrap" } }}
    />
    {/* No auto-hide and no click-away: the list stays until the user closes it. At the top,
        so it never covers the "Uploaded expenses" message at the bottom. */}
    <Snackbar open={offlineDuplicates.length > 0} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
      <Alert
        severity="warning"
        onClose={() => setOfflineDuplicates([])}
        closeText={data.t.read('modal_dialog.close')}
        data-testid="offline-duplicates-notice"
      >
        <AlertTitle>{duplicatesTitle}</AlertTitle>
        {/* A long batch scrolls inside the notice rather than running off a phone screen. */}
        <ul className="mb-0 pl-3" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {offlineDuplicates.map((duplicate, i) => (
            <li key={i}><UploadErrorRow data={data} error={duplicate} /></li>
          ))}
        </ul>
      </Alert>
    </Snackbar>
  </>;
};

export default inject("data")(withRouter(OfflineExpenseUpload));
